import { cn } from '@/lib/utils';
import { Folder, FolderOpen } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import { ThreadHistory, useChatSession } from '@chainlit/react-client';

import { SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar';

import { Translator } from '../i18n';
import FolderOptions from './FolderOptions';
import ThreadOptions from './ThreadOptions';

interface FolderData {
    id: string;
    name: string;
    level: number;
    parentId?: string | null;
    expanded: boolean;
}

interface ThreadFolderAssignment {
    threadId: string;
    folderId: string;
}

interface ProjectFolderItemProps {
    folder: FolderData;
    folders: FolderData[];
    assignments: ThreadFolderAssignment[];
    threadHistory?: ThreadHistory;
    dragOverFolderId?: string;
    dragFolderId?: string;
    draggedThreadId?: string;
    isMobile: boolean;
    isDragging: boolean;
    touchDraggedId?: string;
    contextMenuType: 'thread' | 'folder' | null;
    contextMenuTargetId: string | null;
    folderIdToRename?: string;
    threadIdToRename?: string;
    openDropdownId?: string;
    openThreadDropdownId?: string;
    contextMenuPosition: { x: number; y: number } | null;

    // Event handlers
    onToggleFolder: (folderId: string) => void;
    onFolderDragStart?: (e: React.DragEvent, folderId: string) => void;
    onFolderDragEnd?: () => void;
    onFolderDragOver: (e: React.DragEvent, folderId: string) => void;
    onFolderDragLeave: (folderId: string, e: React.DragEvent) => void;
    onFolderDrop: (e: React.DragEvent, folderId: string) => void;
    onTouchStart?: (
        e: React.TouchEvent,
        id: string,
        type: 'folder' | 'thread'
    ) => void;
    onTouchMove?: (e: React.TouchEvent) => void;
    onTouchEnd?: (e: React.TouchEvent) => void;
    onFolderContextMenu: (e: React.MouseEvent, folderId: string) => void;
    onThreadContextMenu: (e: React.MouseEvent, threadId: string) => void;
    onThreadDragStart?: (e: React.DragEvent, threadId: string) => void;
    onThreadDragEnd?: () => void;

    // Dialog handlers
    onAddSubfolder: (parentId: string) => void;
    onMultiSelectDialog: (folderId: string) => void;
    onRenameFolder: (folderId: string, currentName: string) => void;
    onDeleteFolder: (folderId: string) => void;
    onMoveFolder: (folderId: string, targetParentId: string | null) => void;
    onRenameThread: (threadId: string, name: string) => void;
    onDeleteThread: (threadId: string) => void;
    onAddToProject: (threadId: string, folderId: string) => void;
    onCreateNewProject: (threadId?: string) => void;
    onRemoveThreadFromFolder: (threadId: string) => void;

    // Utilities
    isDescendantOf: (
        parentId: string,
        potentialDescendantId: string
    ) => boolean;
    getThreadsForFolder: (folderId: string) => any[];
    getThreadsForFolderIncludingSubfolders: (folderId: string) => any[];
    getFoldersInTreeOrder: FolderData[];
    setOpenDropdownId: (id: string | undefined) => void;
    setOpenThreadDropdownId: (id: string | undefined) => void;
}

export function ProjectFolderItem({
    folder,
    folders,
    assignments,
    threadHistory,
    dragOverFolderId,
    dragFolderId,
    draggedThreadId,
    isMobile,
    isDragging,
    touchDraggedId,
    contextMenuType,
    contextMenuTargetId,
    folderIdToRename,
    threadIdToRename,
    openDropdownId,
    openThreadDropdownId,
    contextMenuPosition,
    onToggleFolder,
    onFolderDragStart,
    onFolderDragEnd,
    onFolderDragOver,
    onFolderDragLeave,
    onFolderDrop,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onFolderContextMenu,
    onThreadContextMenu,
    onThreadDragStart,
    onThreadDragEnd,
    onAddSubfolder,
    onMultiSelectDialog,
    onRenameFolder,
    onDeleteFolder,
    onMoveFolder,
    onRenameThread,
    onDeleteThread,
    onAddToProject,
    onCreateNewProject,
    onRemoveThreadFromFolder,
    isDescendantOf,
    getThreadsForFolder,
    getThreadsForFolderIncludingSubfolders,
    getFoldersInTreeOrder,
    setOpenDropdownId,
    setOpenThreadDropdownId
}: ProjectFolderItemProps) {
    const { idToResume } = useChatSession();
    const isOnThreadRoute = useLocation().pathname.startsWith('/thread/');

    const containedThreads = getThreadsForFolder(folder.id);
    const totalThreadsIncludingSubfolders =
        getThreadsForFolderIncludingSubfolders(folder.id);

    // Don't show drag-over if the thread is already in this folder
    const isThreadAlreadyInFolder =
        draggedThreadId &&
        assignments.some(
            (a) => a.threadId === draggedThreadId && a.folderId === folder.id
        );

    // Show drag-over indicator when:
    // 1. Dragging a thread over this folder (and thread not already in it)
    // 2. Dragging a folder over this folder (to create subfolder)
    // Important: Only show if we're actually dragging something AND it matches this folder
    const isDragOver = Boolean(
        dragOverFolderId === folder.id &&
            ((draggedThreadId && !isThreadAlreadyInFolder) ||
                (dragFolderId && dragFolderId !== folder.id))
    );

    const isBeingDragged = dragFolderId === folder.id;
    const isBeingTouchDragged =
        isMobile && isDragging && touchDraggedId === folder.id;

    const indentPx = folder.level * 20;
    const canHaveSubfolder = folder.level < 3;

    return (
        <SidebarMenuItem className="flex" key={folder.id}>
            <div
                className={cn(
                    'flex flex-col w-full duration-200',
                    folder.level > 0 && 'border-l border-border'
                )}
                data-folder-id={folder.id}
                style={{
                    marginLeft: indentPx,
                    borderRadius: 0
                }}
                onDragOver={(e) => onFolderDragOver(e, folder.id)}
                onDrop={(e) => onFolderDrop(e, folder.id)}
                onDragLeave={(e) => onFolderDragLeave(folder.id, e)}
            >
                <SidebarMenuButton
                    isActive={false}
                    className={cn(
                        'relative group/folder text-left py-0 items-stretch h-9.5 pl-3 pr-1.25 rounded-lg',
                        ' duration-200',
                        'min-w-0 flex gap-3 font-normal',
                        isDragOver && 'bg-sidebar-accent',
                        (isBeingDragged || isBeingTouchDragged) &&
                            (isMobile
                                ? 'opacity-30 scale-95 duration-150'
                                : 'opacity-50 bg-muted/50 scale-95')
                    )}
                    onClick={() => onToggleFolder(folder.id)}
                    draggable={!isMobile}
                    onDragStart={
                        !isMobile && onFolderDragStart
                            ? (e) => {
                                  console.log(
                                      '🚀 SidebarMenuButton onDragStart triggered for folder:',
                                      folder.id
                                  );
                                  onFolderDragStart(e, folder.id);
                              }
                            : undefined
                    }
                    onDragEnd={!isMobile ? onFolderDragEnd : undefined}
                    onTouchStart={
                        isMobile && onTouchStart
                            ? (e) => onTouchStart(e, folder.id, 'folder')
                            : undefined
                    }
                    onTouchMove={isMobile ? onTouchMove : undefined}
                    onTouchEnd={isMobile ? onTouchEnd : undefined}
                    onContextMenu={(e) => {
                        if (isMobile) {
                            e.preventDefault();
                            return;
                        }
                        onFolderContextMenu(e, folder.id);
                    }}
                    data-context-menu-trigger="true"
                >
                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                        <div
                            className={cn(
                                'size-5! shrink-0 flex items-center justify-center '
                            )}
                        >
                            {folder.expanded ? (
                                <FolderOpen className="size-5!" strokeWidth={1.5} />
                            ) : (
                                <Folder className="size-5!" strokeWidth={1.5} />
                            )}
                        </div>

                        <span className="font-normal w-0 grow min-w-0 flex-1 truncate">
                            {folder.name}
                        </span>
                        {totalThreadsIncludingSubfolders.length > 0 && (
                            <span className="self-center pr-0.5 text-sm tabular-nums shrink-0">
                                {totalThreadsIncludingSubfolders.length}
                            </span>
                        )}
                    </div>

                    <div className="flex items-stretch shrink-0">
                        <div
                            className={cn(
                                'flex items-stretch w-0 overflow-clip transition-[width] duration-200',
                                // Hide when context menu is open or rename dialog is open for this folder
                                isMobile
                                    ? 'w-7'
                                    : (contextMenuType === 'folder' &&
                                          contextMenuTargetId === folder.id) ||
                                      folderIdToRename === folder.id
                                    ? 'pointer-events-none'
                                    : openDropdownId === folder.id
                                    ? 'w-7'
                                    : 'group-hover/folder:w-7'
                            )}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <FolderOptions
                                folder={folder}
                                folders={folders || []}
                                onAddSubfolder={(parentId) =>
                                    onAddSubfolder(parentId)
                                }
                                onAddChats={onMultiSelectDialog}
                                onRename={(folderId, currentName) =>
                                    onRenameFolder(folderId, currentName)
                                }
                                onDelete={onDeleteFolder}
                                onMove={(folderId, targetParentId) =>
                                    onMoveFolder(folderId, targetParentId)
                                }
                                isDescendantOf={isDescendantOf}
                                isOpen={
                                    openDropdownId === folder.id &&
                                    !contextMenuPosition
                                }
                                onOpenChange={(open) => {
                                    if (!contextMenuPosition) {
                                        setOpenDropdownId(
                                            open ? folder.id : undefined
                                        );
                                    }
                                }}
                            />
                        </div>
                    </div>
                </SidebarMenuButton>

                {folder.expanded && containedThreads.length > 0 ? (
                    <div className="ml-5 border-l border-border">
                        {containedThreads.map((thread) => {
                            const isResumed =
                                idToResume === thread!.id &&
                                !threadHistory!.currentThreadId;
                            const isSelected =
                                isOnThreadRoute &&
                                (isResumed ||
                                    threadHistory!.currentThreadId ===
                                        thread!.id);
                            const isBeingTouchDragged =
                                isMobile &&
                                isDragging &&
                                touchDraggedId === thread!.id;

                            return (
                                <SidebarMenuItem
                                    key={thread!.id}
                                    className={cn(
                                        'flex items-center group/thread rounded-lg duration-200 ml-1',
                                        'pr-0 py-0',
                                        isSelected && '  ',
                                        isBeingTouchDragged &&
                                            isMobile &&
                                            'opacity-30 scale-95 duration-150'
                                    )}
                                >
                                    <Link
                                        to={
                                            isResumed
                                                ? ''
                                                : `/thread/${thread!.id}`
                                        }
                                        className="flex-1 min-w-0"
                                        draggable={!isMobile}
                                        onDragStart={
                                            !isMobile && onThreadDragStart
                                                ? (e) =>
                                                      onThreadDragStart(
                                                          e,
                                                          thread!.id
                                                      )
                                                : undefined
                                        }
                                        onDragEnd={
                                            !isMobile
                                                ? onThreadDragEnd
                                                : undefined
                                        }
                                        onTouchStart={
                                            isMobile && onTouchStart
                                                ? (e) =>
                                                      onTouchStart(
                                                          e,
                                                          thread!.id,
                                                          'thread'
                                                      )
                                                : undefined
                                        }
                                        onTouchMove={
                                            isMobile ? onTouchMove : undefined
                                        }
                                        onTouchEnd={
                                            isMobile ? onTouchEnd : undefined
                                        }
                                        onContextMenu={(e) => {
                                            if (isMobile) {
                                                e.preventDefault();
                                                return;
                                            }
                                            onThreadContextMenu(e, thread!.id);
                                        }}
                                        data-context-menu-trigger="true"
                                    >
                                        <SidebarMenuButton
                                            isActive={isSelected}
                                            className={cn(
                                                'relative items-stretch group/thread-button min-w-0 h-9.5 rounded-lg font-normal pl-3 pr-1.25 py-0',
                                                ' duration-200',
                                                isSelected && '  '
                                            )}
                                        >
                                            <div className="flex items-center gap-2.5 min-w-0 flex-1">
                                                <span className="flex justify-center items-center size-3 shrink-0">
                                                    <span
                                                        className={cn(
                                                            'rounded-full size-2 transition-colors',
                                                            isSelected
                                                                ? 'bg-foreground/60'
                                                                : 'border border-muted-foreground/50'
                                                        )}
                                                    />
                                                </span>
                                                <span
                                                    key={thread!.name}
                                                    className=" truncate min-w-0 w-0 grow flex-1 animate-in fade-in slide-in-from-top-1 duration-300 ease-out"
                                                >
                                                    {thread!.name || (
                                                        <span className="italic ">
                                                            <Translator path="threadHistory.thread.untitled" />
                                                        </span>
                                                    )}
                                                </span>
                                            </div>
                                            {/* Thread Actions Dropdown */}
                                            <div
                                                className={cn(
                                                    'flex items-stretch w-0 overflow-clip transition-[width] duration-200',
                                                    'group-hover/thread:w-7',
                                                    // Hide when context menu is open or rename dialog is open for this thread
                                                    isMobile
                                                        ? 'w-7'
                                                        : (contextMenuType ===
                                                              'thread' &&
                                                              contextMenuTargetId ===
                                                                  thread!.id) ||
                                                          threadIdToRename ===
                                                              thread!.id
                                                        ? 'opacity-0 pointer-events-none'
                                                        : openThreadDropdownId ===
                                                          thread!.id
                                                        ? 'w-7'
                                                        : ''
                                                )}
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                            >
                                                <ThreadOptions
                                                    onDelete={() =>
                                                        onDeleteThread(
                                                            thread!.id
                                                        )
                                                    }
                                                    onRename={() =>
                                                        onRenameThread(
                                                            thread!.id,
                                                            thread!.name
                                                        )
                                                    }
                                                    onAddToProject={(
                                                        folderId: string
                                                    ) =>
                                                        onAddToProject(
                                                            thread!.id,
                                                            folderId
                                                        )
                                                    }
                                                    onCreateNewProject={() =>
                                                        onCreateNewProject(
                                                            thread!.id
                                                        )
                                                    }
                                                    onRemoveFromProject={() =>
                                                        onRemoveThreadFromFolder(
                                                            thread!.id
                                                        )
                                                    }
                                                    folders={
                                                        getFoldersInTreeOrder
                                                    }
                                                    isInProject={true}
                                                    isOpen={
                                                        openThreadDropdownId ===
                                                            thread!.id &&
                                                        !contextMenuPosition
                                                    }
                                                    onOpenChange={(open) => {
                                                        if (
                                                            !contextMenuPosition
                                                        ) {
                                                            setOpenThreadDropdownId(
                                                                open
                                                                    ? thread!.id
                                                                    : undefined
                                                            );
                                                        }
                                                    }}
                                                    className={cn(
                                                        'rounded-md',
                                                        'duration-200',
                                                        'focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0',
                                                        'data-[state=open]:opacity-100'
                                                    )}
                                                />
                                            </div>
                                        </SidebarMenuButton>
                                    </Link>
                                </SidebarMenuItem>
                            );
                        })}
                    </div>
                ) : null}
            </div>
        </SidebarMenuItem>
    );
}
